import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { crmApi, extractCrmError, extractCrmErrorPayload } from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCrmDateTime, parseCrmDateTime } from "@/utils/crmDateTime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  LayoutDashboard,
  Link2,
  Loader2,
  QrCode,
  Receipt,
  RefreshCcw,
  ScrollText,
  Settings,
  ShieldAlert,
  Smartphone,
  XCircle,
  Plus,
  Trash2,
  ShoppingCart,
} from "lucide-react";

const DEFAULT_TEST_INVOICE_ITEMS = [
  { name: "Фильтр масляный", price: "3500", count: "1" },
  { name: "Свечи зажигания", price: "1000", count: "2" },
];

const DEFAULT_TEST_QR_CART_ITEMS = [
  {
    name: "Автоодеяло (брезент, 130*80 см) SKYWAY",
    price: "50",
    count: "1",
    gtin: "4627085278191",
    ntin: "0200304733554",
    product_code: "00052142",
  },
];

function formatKaspiPhoneInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
}

function extractKaspiOperationId(result) {
  return result?.Meta?.operationId || result?.Data?.Id || result?.Data?.QrOperationId || null;
}

function formatKzt(value) {
  const amount = Number(value) || 0;
  return `${amount.toLocaleString("ru-RU")} ₸`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const raw = String(value).trim();
  const dmy = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[ T](\d{2}:\d{2}(?::\d{2})?))?/);
  if (dmy) {
    const [, dd, mm, yyyy, time = "00:00:00"] = dmy;
    const parsed = parseCrmDateTime(`${yyyy}-${mm}-${dd}T${time}+05:00`);
    if (parsed) {
      return parsed.toLocaleString("ru-RU", {
        timeZone: "Asia/Almaty",
        dateStyle: "short",
        timeStyle: "short",
      });
    }
  }
  return formatCrmDateTime(value);
}

function transactionRowKey(row) {
  return `${row.source || "x"}-${row.operation_id || row.payment_id}`;
}

function canShowKaspiReceipt(row) {
  if (!row?.receipt_url) return false;
  return row.status === "paid" || row.status === "refunded";
}

function getVisibleTransactionItems(items) {
  const ids = new Set(items.map((row) => String(row.operation_id || "")).filter(Boolean));
  return items.filter((row) => !row.parent_operation_id || !ids.has(String(row.parent_operation_id)));
}

function RelatedReturnsSummary({ row }) {
  const returns = Array.isArray(row?.related_returns) ? row.related_returns : [];
  if (!returns.length) return null;

  return (
    <div className="border-b bg-violet-50/40 px-5 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-violet-900">Возвраты по этой оплате</span>
        <Badge variant="outline" className="border-violet-200 bg-white text-violet-700">
          {row.returns_count || returns.length} на {formatKzt(row.returns_amount_kzt)}
        </Badge>
        {row.net_amount_kzt !== null && row.net_amount_kzt !== undefined ? (
          <span className="text-slate-600">Остаток: {formatKzt(row.net_amount_kzt)}</span>
        ) : null}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {returns.map((item) => (
          <div key={item.operation_id} className="rounded-md border border-violet-100 bg-white p-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-slate-600">{item.operation_id}</span>
              <span className="font-medium text-violet-700">{formatKzt(item.amount)}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{formatDateTime(item.occurred_at)}</div>
            {item.receipt_url ? (
              <a
                href={item.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <Receipt className="h-3.5 w-3.5" />
                Чек возврата
              </a>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function TransactionDetailsPanel({ token, row, onUpdated }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundAmount, setRefundAmount] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadDetails = useCallback(async () => {
    if (!token || !row?.operation_id) return;
    setLoading(true);
    try {
      const payload = await crmApi.getKaspiTransactionDetails(token, row.operation_id, {
        source: row.source,
        paymentMethod: row.payment_method,
        operationMethod: row.operation_method,
      });
      setDetails(payload);
      const defaultAmount = payload.available_return_amount || row.amount || 0;
      setRefundAmount(String(defaultAmount > 0 ? defaultAmount : row.amount || ""));
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить детали"));
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [token, row]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const handleRefund = async () => {
    const amount = Math.round(Number(refundAmount));
    if (!amount || amount <= 0) {
      toast.error("Укажите сумму возврата");
      return;
    }
    if (!window.confirm(`Оформить возврат ${formatKzt(amount)} по операции ${row.operation_id}?`)) {
      return;
    }
    setRefunding(true);
    try {
      await crmApi.refundKaspiTransaction(token, row.operation_id, {
        amount,
        payment_id: row.payment_id || undefined,
      });
      toast.success("Возврат оформлен");
      await loadDetails();
      onUpdated?.();
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось оформить возврат"));
    } finally {
      setRefunding(false);
    }
  };

  const handleCancelInvoice = async () => {
    if (!window.confirm(`Отменить счёт по операции ${row.operation_id}?`)) {
      return;
    }
    setCancelling(true);
    try {
      await crmApi.cancelKaspiTransaction(token, row.operation_id, {
        payment_id: row.payment_id || undefined,
      });
      toast.success("Счёт отменён");
      await loadDetails();
      onUpdated?.();
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось отменить счёт"));
    } finally {
      setCancelling(false);
    }
  };

  const displayOccurredAt = details?.occurred_at || row.occurred_at;
  const canRefund = details?.can_refund || row.can_refund;
  const canCancel =
    details?.can_cancel
    || row.can_cancel
    || (
      (row.source === "kaspi_invoice" || row.payment_method === "kaspi_invoice")
      && row.status === "pending"
    );

  return (
    <div className="space-y-4 bg-slate-50/80 px-4 py-4">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка деталей...
        </div>
      ) : (
        <>
          {details?.error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {details.error}
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg border bg-white p-3 text-sm">
              <p className="text-xs uppercase text-slate-500">Дата и время</p>
              <p className="mt-1 font-medium">{formatDateTime(displayOccurredAt)}</p>
            </div>
            <div className="rounded-lg border bg-white p-3 text-sm">
              <p className="text-xs uppercase text-slate-500">Operation ID</p>
              <p className="mt-1 font-mono text-xs">{row.operation_id || "—"}</p>
            </div>
            <div className="rounded-lg border bg-white p-3 text-sm">
              <p className="text-xs uppercase text-slate-500">Источник</p>
              <p className="mt-1 font-medium">{row.source || "—"}</p>
            </div>
          </div>

          {details?.fields?.length > 0 ? (
            <div className="rounded-lg border bg-white p-4">
              <p className="mb-3 text-sm font-semibold text-slate-700">Детали Kaspi</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {details.fields.map((field) => (
                  <div key={`${field.label}-${field.value}`} className="rounded-md bg-slate-50 px-3 py-2 text-sm">
                    <p className="text-xs text-slate-500">{field.label}</p>
                    {field.label === "Чек" && String(field.value).startsWith("http") ? (
                      <a
                        href={field.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-block text-blue-600 hover:underline"
                      >
                        Открыть чек
                      </a>
                    ) : (
                      <p className="mt-1 break-words font-medium">{field.value}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {row.extra && Object.keys(row.extra).length > 0 ? (
            <div className="rounded-lg border bg-white p-4">
              <p className="mb-3 text-sm font-semibold text-slate-700">Дополнительно из списка</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {Object.entries(row.extra).map(([key, value]) => (
                  <div key={key} className="rounded-md bg-slate-50 px-3 py-2 text-sm">
                    <p className="text-xs text-slate-500">{key}</p>
                    <p className="mt-1 break-words font-medium">{String(value)}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {row.comment ? (
            <div className="rounded-lg border bg-white p-3 text-sm">
              <p className="text-xs uppercase text-slate-500">Комментарий CRM</p>
              <p className="mt-1">{row.comment}</p>
            </div>
          ) : null}

          {canCancel ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-900">Отмена счёта</p>
              <p className="mt-1 text-xs text-red-800">
                Счёт ожидает оплаты и может быть отменён в Kaspi.
              </p>
              <div className="mt-3">
                <Button variant="destructive" disabled={cancelling || refunding} onClick={handleCancelInvoice}>
                  {cancelling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}
                  Отменить счёт
                </Button>
              </div>
            </div>
          ) : null}

          {!canRefund && details?.refund_disabled_reason ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold">Возврат через CRM недоступен</p>
              <p className="mt-1 text-xs leading-relaxed">{details.refund_disabled_reason}</p>
            </div>
          ) : null}

          {canRefund ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
              <p className="text-sm font-semibold text-violet-900">Возврат</p>
              <p className="mt-1 text-xs text-violet-800">
                {details?.available_return_amount
                  ? `Доступно к возврату: ${formatKzt(details.available_return_amount)}`
                  : "Доступен возврат по QR/POS операции"}
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="space-y-2">
                  <Label htmlFor={`refund-${row.operation_id}`}>Сумма возврата, ₸</Label>
                  <Input
                    id={`refund-${row.operation_id}`}
                    type="number"
                    min="1"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button variant="secondary" disabled={refunding || cancelling} onClick={handleRefund}>
                  {refunding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Оформить возврат
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function todayIso() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function StatusBadge({ ok, label, detail }) {
  const variant = ok === null ? "border-slate-200 bg-slate-50 text-slate-600" : ok
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : "border-red-200 bg-red-50 text-red-700";
  return (
    <div className={`rounded-lg border p-4 ${variant}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-base font-semibold">{ok === null ? "—" : ok ? "OK" : "Проблема"}</p>
      {detail ? <p className="mt-1 text-xs opacity-80">{detail}</p> : null}
    </div>
  );
}

function StatCard({ label, value, hint, accent }) {
  const accentClass = accent === "green"
    ? "border-emerald-200 bg-emerald-50"
    : accent === "amber"
      ? "border-amber-200 bg-amber-50"
      : "border-slate-200 bg-slate-50";
  return (
    <div className={`rounded-lg border p-4 ${accentClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {hint ? <p className="mt-1 text-xs text-slate-600">{hint}</p> : null}
    </div>
  );
}

function TransactionStatusBadge({ status, label }) {
  const cls = status === "paid"
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : status === "pending"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : status === "failed"
        ? "border-red-200 bg-red-50 text-red-700"
        : status === "refunded"
          ? "border-violet-200 bg-violet-50 text-violet-700"
          : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <Badge className={cls} variant="outline">
      {label || status || "—"}
    </Badge>
  );
}

function KaspiOverviewTab({ token, selectedDay, onDayChange }) {
  const [health, setHealth] = useState(null);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [healthData, settingsData, statsData] = await Promise.all([
        crmApi.getKaspiHealth(token),
        crmApi.getKaspiDashboardSettings(token),
        crmApi.getKaspiDashboardStats(token, selectedDay),
      ]);
      setHealth(healthData);
      setSettings(settingsData);
      setStats(statsData);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить обзор Kaspi"));
    } finally {
      setLoading(false);
    }
  }, [token, selectedDay]);

  useEffect(() => {
    load();
  }, [load]);

  const session = settings?.session || health?.session || {};
  const gateway = health?.gateway || {};
  const crm = stats?.crm || {};
  const combined = stats?.combined || {};
  const kaspiApi = stats?.kaspi_api;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <Label htmlFor="kaspi-stats-day">Дата статистики</Label>
          <Input
            id="kaspi-stats-day"
            type="date"
            value={selectedDay}
            onChange={(e) => onDayChange(e.target.value)}
            className="w-44"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Обновить
        </Button>
      </div>

      {loading && !stats ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Оплачено"
              value={formatKzt(combined.paid_amount_kzt)}
              hint={`${combined.paid_count || 0} операций за день`}
              accent="green"
            />
            <StatCard
              label="Ожидают оплаты"
              value={formatKzt(combined.pending_amount_kzt)}
              hint={`${combined.pending_count || 0} счетов/QR`}
              accent="amber"
            />
            <StatCard
              label="Возвраты"
              value={formatKzt(combined.returns_amount_kzt)}
              hint={`${combined.returns_count || 0} возвратов`}
            />
            <StatCard
              label="Чистая выручка"
              value={formatKzt(combined.net_amount_kzt)}
              hint={`${combined.total_count || 0} транзакций за день`}
              accent="green"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Привязано к заказам"
              value={combined.linked_to_orders_count ?? 0}
              hint="оплат из CRM за день"
            />
            <StatCard
              label="Ошибки / отклонено"
              value={combined.failed_count ?? 0}
              hint={formatKzt(combined.failed_amount_kzt)}
            />
            <StatCard
              label="Kaspi API продажи"
              value={kaspiApi ? formatKzt(kaspiApi.sales_amount_kzt) : "—"}
              hint={kaspiApi ? `${kaspiApi.sales_count || 0} по отчёту Kaspi` : stats?.kaspi_api_error || "Сверка с API"}
            />
            <StatCard
              label="Только CRM"
              value={formatKzt(crm.paid_amount_kzt)}
              hint={`${crm.paid_count || 0} оплат в базе`}
            />
          </div>

          {(combined.by_status?.length > 0 || combined.by_method?.length > 0) && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {combined.by_status?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">По статусам за день</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {combined.by_status.map((row) => (
                      <div key={row.status} className="flex items-center justify-between text-sm">
                        <span>{row.status_label}</span>
                        <span className="font-medium">
                          {row.count} · {formatKzt(row.amount_kzt)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : null}
              {combined.by_method?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">По способам оплаты</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {combined.by_method.map((row) => (
                      <div key={row.method} className="flex items-center justify-between text-sm">
                        <span>{row.method_label}</span>
                        <span className="font-medium">
                          {row.count} · {formatKzt(row.amount_kzt)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : null}
            </div>
          )}

          {(crm.by_status?.length > 0 || crm.by_method?.length > 0) && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {crm.by_status?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">CRM по статусам</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {crm.by_status.map((row) => (
                      <div key={row.status} className="flex items-center justify-between text-sm">
                        <span>{row.status_label}</span>
                        <span className="font-medium">
                          {row.count} · {formatKzt(row.amount_kzt)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : null}
              {crm.by_method?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">CRM по способам</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {crm.by_method.map((row) => (
                      <div key={row.method} className="flex items-center justify-between text-sm">
                        <span>{row.method_label}</span>
                        <span className="font-medium">
                          {row.count} · {formatKzt(row.amount_kzt)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : null}
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Состояние интеграции</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline">{settings?.enabled ? "Интеграция включена" : "Интеграция выключена"}</Badge>
                <Badge variant="outline">{settings?.webhook_configured ? "Webhook настроен" : "Webhook не настроен"}</Badge>
                {settings?.base_url ? (
                  <a
                    href={settings.base_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  >
                    {settings.base_url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatusBadge
                  label="Gateway"
                  ok={gateway.reachable ?? null}
                  detail={gateway.reachable ? "kaspi-pos отвечает" : gateway.error || "Недоступен"}
                />
                <StatusBadge
                  label="Сессия"
                  ok={session.configured ? session.active !== false : false}
                  detail={
                    session.configured
                      ? [session.org_name, session.phone].filter(Boolean).join(" · ") || "Настроена"
                      : "Не авторизован"
                  }
                />
                <StatusBadge
                  label="Profile ID"
                  ok={session.profile_id ? true : null}
                  detail={session.profile_id || "—"}
                />
                <StatusBadge
                  label="Обновлено"
                  ok={session.updated_at ? true : null}
                  detail={session.updated_at ? formatDateTime(session.updated_at) : session.error || "—"}
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KaspiTransactionsTab({ token, selectedDay, onDayChange }) {
  const [transactions, setTransactions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setTransactions(await crmApi.getKaspiDashboardTransactions(token, { day: selectedDay, limit: 200 }));
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить транзакции"));
      setTransactions(null);
    } finally {
      setLoading(false);
    }
  }, [token, selectedDay]);

  useEffect(() => {
    load();
  }, [load]);

  const items = transactions?.items || [];
  const visibleItems = getVisibleTransactionItems(items);
  const groupedCount = items.length - visibleItems.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <Label htmlFor="kaspi-tx-day">Дата</Label>
          <Input
            id="kaspi-tx-day"
            type="date"
            value={selectedDay}
            onChange={(e) => onDayChange(e.target.value)}
            className="w-44"
          />
        </div>
        <div className="flex items-center gap-3">
          {transactions ? (
            <span className="text-sm text-slate-600">
              Всего: {visibleItems.length}
              {groupedCount > 0 ? `, скрыто возвратов: ${groupedCount}` : ""}
            </span>
          ) : null}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Обновить
          </Button>
        </div>
      </div>

      {transactions?.kaspi_api_error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Kaspi API: {transactions.kaspi_api_error}
        </div>
      ) : null}

      {loading && !transactions ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка...
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
          Нет транзакций за выбранный день
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-3 w-10" />
                <th className="px-4 py-3">Время</th>
                <th className="px-4 py-3">Сумма</th>
                <th className="px-4 py-3">Способ</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3">Клиент</th>
                <th className="px-4 py-3">Заказ</th>
                <th className="px-4 py-3">Operation ID</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibleItems.map((row) => {
                const key = transactionRowKey(row);
                const expanded = expandedKey === key;
                const hasReturns = Number(row.returns_count || 0) > 0;
                return (
                  <React.Fragment key={key}>
                    <tr
                      className="hover:bg-slate-50/80 cursor-pointer"
                      onClick={() => setExpandedKey(expanded ? null : key)}
                    >
                      <td className="px-2 py-3 text-slate-400">
                        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(row.occurred_at)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium">{formatKzt(row.amount)}</div>
                        {hasReturns ? (
                          <div className="text-xs text-violet-700">
                            Возврат: {formatKzt(row.returns_amount_kzt)}
                            {row.net_amount_kzt !== null && row.net_amount_kzt !== undefined
                              ? ` · остаток ${formatKzt(row.net_amount_kzt)}`
                              : ""}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">{row.method_label || row.payment_method || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <TransactionStatusBadge status={row.status} label={row.status_label} />
                          {hasReturns ? (
                            <Badge variant="outline" className="w-fit border-violet-200 bg-violet-50 text-xs text-violet-700">
                              есть возврат
                            </Badge>
                          ) : null}
                          {row.kaspi_status_label && row.kaspi_status_label !== row.status_label ? (
                            <span className="text-xs text-slate-500">{row.kaspi_status_label}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">{row.client_name || "—"}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {row.linked_to_order && row.order_id ? (
                          <Link
                            to={`/crm/customer-orders/${row.order_id}/edit`}
                            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            <Link2 className="h-3.5 w-3.5" />
                            {row.order_number || `#${row.order_id}`}
                          </Link>
                        ) : (
                          <span className="text-slate-400">Без заказа</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">
                        <div>{row.operation_id || "—"}</div>
                        {hasReturns ? (
                          <div className="mt-1 max-w-56 whitespace-normal font-sans text-xs text-violet-700">
                            Возвраты: {(row.return_operation_ids || []).join(", ")}
                          </div>
                        ) : row.parent_operation_id ? (
                          <div className="mt-1 font-sans text-xs text-violet-700">
                            К оплате {row.parent_operation_id}
                          </div>
                        ) : null}
                        {canShowKaspiReceipt(row) ? (
                          <a
                            href={row.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Чек
                          </a>
                        ) : null}
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <RelatedReturnsSummary row={row} />
                          <TransactionDetailsPanel token={token} row={row} onUpdated={load} />
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function logLineClass(line) {
  if (line.includes("[ERROR]")) return "text-red-400";
  if (line.includes("[WARN]")) return "text-amber-300";
  if (line.includes("[HTTP]")) return "text-sky-300";
  if (line.includes("[API]")) return "text-cyan-300";
  if (line.includes("[WEBHOOK]")) return "text-violet-300";
  if (line.includes("[POLLING]")) return "text-slate-300";
  return "text-slate-200";
}

function KaspiLogsTab({ token, selectedDay, onDayChange }) {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setLogs(await crmApi.getKaspiLogs(token, { day: selectedDay, limit: 300 }));
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить логи Kaspi"));
      setLogs(null);
    } finally {
      setLoading(false);
    }
  }, [token, selectedDay]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const lines = logs?.lines || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <Label htmlFor="kaspi-logs-day">Дата логов</Label>
          <Input
            id="kaspi-logs-day"
            type="date"
            value={selectedDay}
            onChange={(e) => onDayChange(e.target.value)}
            className="w-44"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {logs ? (
            <span className="text-sm text-slate-600">
              {logs.file ? `${logs.file}: ` : ""}{logs.total} строк, показано {lines.length}
            </span>
          ) : null}
          <Button
            variant={autoRefresh ? "secondary" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
          >
            {autoRefresh ? "Авто 5с" : "Авто выкл"}
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Обновить
          </Button>
        </div>
      </div>

      {logs?.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {logs.error}
        </div>
      ) : null}

      <div className="rounded-lg border bg-slate-950">
        {loading && !logs ? (
          <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загрузка логов kaspi-pos...
          </div>
        ) : lines.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            Логов за выбранный день пока нет. Запросы к Kaspi и микросервису будут появляться здесь.
          </div>
        ) : (
          <pre className="max-h-[560px] overflow-auto p-4 text-xs leading-relaxed whitespace-pre-wrap">
            {lines.map((line, idx) => (
              <div key={`${idx}-${line.slice(0, 24)}`} className={logLineClass(line)}>
                {line}
              </div>
            ))}
          </pre>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Логи микросервиса kaspi-pos: входящие API-запросы, HTTP к Kaspi, polling платежей и webhooks.
      </p>
    </div>
  );
}

function KaspiSettingsTab({ token, health, loading, loadHealth }) {
  const [busy, setBusy] = useState(false);
  const [processId, setProcessId] = useState("");
  const [authStep, setAuthStep] = useState("idle");
  const [cashierPhone, setCashierPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testAmount, setTestAmount] = useState("100");
  const [testComment, setTestComment] = useState("Тест из админки");
  const [testItemsComment, setTestItemsComment] = useState("Тест счёта с позициями");
  const [testItems, setTestItems] = useState(DEFAULT_TEST_INVOICE_ITEMS);
  const [testCartItems, setTestCartItems] = useState(DEFAULT_TEST_QR_CART_ITEMS);
  const [cancelOperationId, setCancelOperationId] = useState("");
  const [operationType, setOperationType] = useState("invoice");
  const [operationStatus, setOperationStatus] = useState(null);
  const [trackedOp, setTrackedOp] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const refreshOperationStatus = useCallback(async (operationId, paymentType) => {
    if (!token || !operationId) return null;
    try {
      const status = await crmApi.kaspiGetOperationStatus(token, operationId, paymentType);
      setOperationStatus(status);
      return status;
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось получить статус операции"));
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (!trackedOp?.id || trackedOp.final) return undefined;
    refreshOperationStatus(trackedOp.id, trackedOp.type);
    const timer = setInterval(() => {
      refreshOperationStatus(trackedOp.id, trackedOp.type).then((status) => {
        if (status?.is_final) {
          setTrackedOp((prev) => (prev ? { ...prev, final: true } : prev));
        }
      });
    }, 3000);
    return () => clearInterval(timer);
  }, [trackedOp, refreshOperationStatus]);

  const runAction = async (action, successMessage) => {
    setBusy(true);
    try {
      const result = await action();
      setLastResult(result);
      if (successMessage) toast.success(successMessage);
      return result;
    } catch (err) {
      const payload = extractCrmErrorPayload(err);
      setLastResult(typeof payload === "string" ? { error: payload } : payload);
      toast.error(extractCrmError(err, "Операция не выполнена"));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleStartAuth = () =>
    runAction(async () => {
      const result = await crmApi.kaspiAuthInit(token);
      if (!result?.process_id && !result?.processId) {
        throw new Error("Kaspi не вернул processId");
      }
      const pid = result.process_id || result.processId;
      setProcessId(pid);
      setAuthStep("phone");
      return result;
    }, "Авторизация начата — введите номер кассира");

  const handleSendPhone = () =>
    runAction(async () => {
      const digits = cashierPhone.replace(/\D/g, "").slice(-10);
      if (digits.length < 10) throw new Error("Введите 10 цифр номера без +7");
      const result = await crmApi.kaspiAuthSendPhone(token, {
        phone_number: digits,
        process_id: processId,
      });
      setAuthStep("otp");
      return result;
    }, "SMS-код отправлен");

  const handleVerifyOtp = () =>
    runAction(async () => {
      const result = await crmApi.kaspiAuthVerifyOtp(token, {
        otp: otp.trim(),
        process_id: processId,
      });
      setAuthStep("done");
      setOtp("");
      await loadHealth();
      return result;
    }, "Kaspi-сессия сохранена");

  const handleSessionCheck = () =>
    runAction(async () => {
      const result = await crmApi.kaspiSessionCheck(token);
      await loadHealth();
      return result;
    }, "Проверка сессии выполнена");

  const handleTestInvoice = () =>
    runAction(async () => {
      const amount = Number(testAmount);
      if (!amount || amount <= 0) throw new Error("Укажите сумму");
      const result = await crmApi.kaspiTestInvoice(token, {
        phone_number: testPhone.replace(/\D/g, "").slice(-10),
        amount: Math.round(amount),
        comment: testComment.trim() || undefined,
      });
      const opId = extractKaspiOperationId(result);
      if (opId) {
        const id = String(opId);
        setCancelOperationId(id);
        setTrackedOp({ id, type: "invoice", final: false });
        await refreshOperationStatus(id, "invoice");
      }
      return result;
    }, "Тестовый счёт создан");

  const handleTestInvoiceItems = () =>
    runAction(async () => {
      const phone = testPhone.replace(/\D/g, "").slice(-10);
      if (phone.length < 10) throw new Error("Укажите телефон клиента (10 цифр)");
      const items = testItems
        .map((row) => ({
          name: row.name.trim(),
          price: Math.round(Number(row.price)),
          count: Math.round(Number(row.count) || 1),
        }))
        .filter((row) => row.name && row.price > 0 && row.count > 0);
      if (!items.length) throw new Error("Добавьте хотя бы одну позицию с названием и ценой");
      const result = await crmApi.kaspiTestInvoiceItems(token, {
        phone_number: phone,
        comment: testItemsComment.trim() || undefined,
        items,
      });
      const opId = extractKaspiOperationId(result);
      if (opId) {
        const id = String(opId);
        setCancelOperationId(id);
        setTrackedOp({ id, type: "invoice", final: false });
        await refreshOperationStatus(id, "invoice");
      }
      return result;
    }, "Тестовый счёт с позициями создан");

  const testItemsTotal = testItems.reduce((sum, row) => {
    const price = Number(row.price) || 0;
    const count = Number(row.count) || 0;
    return sum + price * count;
  }, 0);

  const updateTestItem = (index, field, value) => {
    setTestItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addTestItem = () => {
    setTestItems((prev) => [...prev, { name: "", price: "", count: "1" }]);
  };

  const removeTestItem = (index) => {
    setTestItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const testCartItemsTotal = testCartItems.reduce((sum, row) => {
    const price = Number(row.price) || 0;
    const count = Number(row.count) || 0;
    return sum + price * count;
  }, 0);

  const updateTestCartItem = (index, field, value) => {
    setTestCartItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addTestCartItem = () => {
    setTestCartItems((prev) => [
      ...prev,
      {
        name: "",
        price: "",
        count: "1",
        gtin: "",
        ntin: "",
        product_code: "",
      },
    ]);
  };

  const removeTestCartItem = (index) => {
    setTestCartItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleTestQrCart = () =>
    runAction(async () => {
      const items = testCartItems
        .map((row) => ({
          name: row.name.trim(),
          price: Math.round(Number(row.price)),
          count: Math.max(1, Math.round(Number(row.count) || 1)),
          gtin: row.gtin.trim(),
          ntin: row.ntin.trim() || undefined,
          product_code: row.product_code.trim(),
        }))
        .filter((row) => row.name && row.price > 0 && row.gtin && row.product_code);
      if (!items.length) {
        throw new Error("Добавьте позиции с названием, ценой, GTIN и кодом товара");
      }
      const result = await crmApi.kaspiTestQrCart(token, { items });
      const opId = extractKaspiOperationId(result);
      if (opId) {
        const id = String(opId);
        setCancelOperationId(id);
        setOperationType("qr");
        setTrackedOp({ id, type: "qr", final: false });
        await refreshOperationStatus(id, "qr");
      }
      const qrToken = result?.Data?.QrToken || result?.Meta?.qrToken;
      if (qrToken) window.open(qrToken, "_blank", "noopener,noreferrer");
      return result;
    }, "QR с корзиной создан — позиции попадут в чек");

  const handleTestQr = () =>
    runAction(async () => {
      const amount = Number(testAmount);
      if (!amount || amount <= 0) throw new Error("Укажите сумму");
      const result = await crmApi.kaspiTestQr(token, { amount: Math.round(amount) });
      const opId = result?.Data?.QrOperationId;
      if (opId) {
        const id = String(opId);
        setCancelOperationId(id);
        setTrackedOp({ id, type: "qr", final: false });
        await refreshOperationStatus(id, "qr");
      }
      const qrToken = result?.Data?.QrToken;
      if (qrToken) window.open(qrToken, "_blank", "noopener,noreferrer");
      return result;
    }, "Тестовый QR создан");

  const handleTestCancel = () =>
    runAction(async () => {
      if (!cancelOperationId.trim()) throw new Error("Укажите operation id");
      return crmApi.kaspiTestCancel(token, { operation_id: cancelOperationId.trim() });
    }, "Операция отменена");

  const handleCheckOperation = () =>
    runAction(async () => {
      if (!cancelOperationId.trim()) throw new Error("Укажите operation id");
      const status = await refreshOperationStatus(cancelOperationId.trim(), operationType);
      if (status) {
        setTrackedOp({
          id: status.operation_id || cancelOperationId.trim(),
          type: operationType,
          final: Boolean(status.is_final),
        });
      }
      return status;
    }, "Статус обновлён");

  const session = health?.session || {};
  const sessionReady = Boolean(session.configured);
  const sessionActive = session.active !== false;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-5 w-5" />
            Авторизация кассира Kaspi
          </CardTitle>
          <CardDescription>
            Номер телефона аккаунта кассира Kaspi Pay. После успешного входа сессия сохраняется на сервере.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {authStep === "done" || (sessionReady && sessionActive) ? (
            <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-medium">Сессия активна</p>
                <p className="mt-1 opacity-80">
                  {session.org_name || "Организация"} · {session.phone || "телефон не указан"}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <p>Для выставления счетов и QR нужна авторизация кассира.</p>
            </div>
          )}

          {authStep === "idle" && (
            <Button onClick={handleStartAuth} disabled={busy || !health?.enabled}>
              Начать авторизацию
            </Button>
          )}

          {(authStep === "phone" || authStep === "otp") && (
            <div className="grid gap-4 md:grid-cols-2">
              {authStep === "phone" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="kaspi-cashier-phone">Телефон кассира</Label>
                    <div className="flex overflow-hidden rounded-md border border-input">
                      <span className="flex items-center bg-slate-50 px-3 text-sm text-slate-600">+7</span>
                      <Input
                        id="kaspi-cashier-phone"
                        className="border-0 focus-visible:ring-0"
                        value={cashierPhone}
                        onChange={(e) => setCashierPhone(formatKaspiPhoneInput(e.target.value))}
                        placeholder="707 564 68 68"
                      />
                    </div>
                    <p className="text-xs text-slate-500">10 цифр без кода страны — как в Kaspi Pay</p>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSendPhone} disabled={busy || !cashierPhone.trim()}>
                      Отправить SMS-код
                    </Button>
                  </div>
                </>
              )}
              {authStep === "otp" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="kaspi-otp">Код из SMS</Label>
                    <Input
                      id="kaspi-otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="1234"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button onClick={handleVerifyOtp} disabled={busy || !otp.trim()}>
                      Подтвердить
                    </Button>
                    <Button variant="ghost" onClick={() => setAuthStep("phone")} disabled={busy}>
                      Назад
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {sessionReady ? (
              <Button variant="outline" onClick={() => { setAuthStep("idle"); setProcessId(""); }} disabled={busy}>
                Переавторизоваться
              </Button>
            ) : null}
            <Button variant="secondary" disabled={busy || loading} onClick={handleSessionCheck}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-2 h-4 w-4" />}
              Проверить сессию и уведомить в Telegram
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <QrCode className="h-5 w-5" />
            Тестовые платежи
          </CardTitle>
          <CardDescription>
            Проверка без привязки к заказу. Статус подтягивается из Kaspi каждые 3 секунды.
            Тестовый счёт не создаёт оплату в CRM — только в приложении Kaspi.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {operationStatus ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">Статус операции:</span>
                <Badge
                  className={
                    operationStatus.status === "Processed"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-amber-200 bg-amber-50 text-amber-800"
                  }
                  variant="outline"
                >
                  {operationStatus.status_label || operationStatus.status || "—"}
                </Badge>
                {operationStatus.order_number ? (
                  <span className="text-slate-600">№ {operationStatus.order_number}</span>
                ) : null}
              </div>
              {operationStatus.status_desc ? (
                <p className="mt-2 text-slate-600">{operationStatus.status_desc}</p>
              ) : null}
              {operationStatus.receipt_url && operationStatus.is_final ? (
                <a
                  href={operationStatus.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-blue-600 hover:underline"
                >
                  Открыть чек
                </a>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-2">
            <Label>Последний ответ Kaspi</Label>
            {lastResult ? (
              <pre className="max-h-72 overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-100 whitespace-pre-wrap">
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            ) : (
              <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                Выполните тестовую операцию — здесь появится ответ Kaspi (в том числе при ошибке).
              </p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="kaspi-test-phone">Телефон клиента (для счёта)</Label>
              <div className="flex overflow-hidden rounded-md border border-input">
                <span className="flex items-center bg-slate-50 px-3 text-sm text-slate-600">+7</span>
                <Input
                  id="kaspi-test-phone"
                  className="border-0 focus-visible:ring-0"
                  value={testPhone}
                  onChange={(e) => setTestPhone(formatKaspiPhoneInput(e.target.value))}
                  placeholder="700 123 45 67"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="kaspi-test-amount">Сумма, ₸</Label>
              <Input
                id="kaspi-test-amount"
                type="number"
                min="1"
                value={testAmount}
                onChange={(e) => setTestAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="kaspi-test-comment">Комментарий</Label>
              <Input
                id="kaspi-test-comment"
                value={testComment}
                onChange={(e) => setTestComment(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleTestInvoice} disabled={busy || !sessionReady || !testPhone.trim()}>
              <Receipt className="mr-2 h-4 w-4" />
              Тест: счёт
            </Button>
            <Button variant="secondary" onClick={handleTestQr} disabled={busy || !sessionReady}>
              <QrCode className="mr-2 h-4 w-4" />
              Тест: QR
            </Button>
          </div>

          <div className="space-y-4 rounded-lg border border-emerald-300 bg-emerald-50/60 p-4">
            <div>
              <p className="flex items-center gap-2 font-medium text-slate-900">
                <QrCode className="h-4 w-4" />
                QR с корзиной (create-with-cart)
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Kaspi <code className="text-xs">v01/qr-token/create-with-cart</code> — в чек попадают GTIN,
                Ntin и наш <strong>code</strong> товара (как NomenclatureId). Успех: в ответе есть{" "}
                <code className="text-xs">CartId</code>, не нули.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Позиции каталога</Label>
                <span className="text-sm text-slate-600">Итого: {formatKzt(testCartItemsTotal)}</span>
              </div>
              <div className="space-y-3">
                {testCartItems.map((row, index) => (
                  <div key={index} className="space-y-2 rounded-md border border-emerald-200 bg-white p-3">
                    <div className="grid gap-2 md:grid-cols-[1fr_100px_70px_auto]">
                      <Input
                        value={row.name}
                        onChange={(e) => updateTestCartItem(index, "name", e.target.value)}
                        placeholder="Название"
                      />
                      <Input
                        type="number"
                        min="1"
                        value={row.price}
                        onChange={(e) => updateTestCartItem(index, "price", e.target.value)}
                        placeholder="Цена"
                      />
                      <Input
                        type="number"
                        min="1"
                        value={row.count}
                        onChange={(e) => updateTestCartItem(index, "count", e.target.value)}
                        placeholder="Кол"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeTestCartItem(index)}
                        disabled={testCartItems.length <= 1}
                        aria-label="Удалить позицию"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                      <Input
                        value={row.gtin}
                        onChange={(e) => updateTestCartItem(index, "gtin", e.target.value)}
                        placeholder="GTIN / штрихкод"
                      />
                      <Input
                        value={row.ntin}
                        onChange={(e) => updateTestCartItem(index, "ntin", e.target.value)}
                        placeholder="Ntin (НКТ)"
                      />
                      <Input
                        value={row.product_code}
                        onChange={(e) => updateTestCartItem(index, "product_code", e.target.value)}
                        placeholder="Код товара (наш id)"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addTestCartItem}>
                <Plus className="mr-2 h-4 w-4" />
                Добавить позицию
              </Button>
            </div>

            <Button onClick={handleTestQrCart} disabled={busy || !sessionReady}>
              <QrCode className="mr-2 h-4 w-4" />
              Тест: QR с корзиной
            </Button>
          </div>

          <div className="space-y-4 rounded-lg border border-dashed border-slate-300 bg-slate-50/80 p-4">
            <div>
              <p className="flex items-center gap-2 font-medium text-slate-900">
                <ShoppingCart className="h-4 w-4" />
                Удалённый счёт с позициями (устарело)
              </p>
              <p className="mt-1 text-sm text-amber-700">
                v01/remote/create + PosItems не создаёт корзину — Kaspi игнорирует позиции. Используйте QR с корзиной
                выше.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="kaspi-test-items-comment">Комментарий к счёту</Label>
              <Input
                id="kaspi-test-items-comment"
                value={testItemsComment}
                onChange={(e) => setTestItemsComment(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Позиции</Label>
                <span className="text-sm text-slate-600">Итого: {formatKzt(testItemsTotal)}</span>
              </div>
              <div className="space-y-2">
                {testItems.map((row, index) => (
                  <div key={index} className="grid gap-2 md:grid-cols-[1fr_120px_80px_auto]">
                    <Input
                      value={row.name}
                      onChange={(e) => updateTestItem(index, "name", e.target.value)}
                      placeholder="Название"
                    />
                    <Input
                      type="number"
                      min="1"
                      value={row.price}
                      onChange={(e) => updateTestItem(index, "price", e.target.value)}
                      placeholder="Цена, ₸"
                    />
                    <Input
                      type="number"
                      min="1"
                      value={row.count}
                      onChange={(e) => updateTestItem(index, "count", e.target.value)}
                      placeholder="Кол-во"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTestItem(index)}
                      disabled={testItems.length <= 1}
                      aria-label="Удалить позицию"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addTestItem}>
                <Plus className="mr-2 h-4 w-4" />
                Добавить позицию
              </Button>
            </div>

            <Button onClick={handleTestInvoiceItems} disabled={busy || !sessionReady || !testPhone.trim()}>
              <ShoppingCart className="mr-2 h-4 w-4" />
              Тест: счёт с позициями
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-[160px_1fr_auto]">
            <div className="space-y-2">
              <Label>Тип</Label>
              <Select value={operationType} onValueChange={setOperationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Счёт</SelectItem>
                  <SelectItem value="qr">QR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="kaspi-cancel-op">Operation ID</Label>
              <Input
                id="kaspi-cancel-op"
                value={cancelOperationId}
                onChange={(e) => setCancelOperationId(e.target.value)}
                placeholder="16023892288 или QR16023892288"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="secondary" onClick={handleCheckOperation} disabled={busy || !cancelOperationId.trim()}>
                <RefreshCcw className="mr-2 h-4 w-4" />
                Статус
              </Button>
              <Button variant="outline" onClick={handleTestCancel} disabled={busy || !cancelOperationId.trim()}>
                Отменить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function KaspiAdminPanel({ token }) {
  const [selectedDay, setSelectedDay] = useState(todayIso());
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadHealth = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setHealth(await crmApi.getKaspiHealth(token));
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить статус Kaspi"));
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  return (
    <div className="space-y-6" data-testid="kaspi-admin-panel">
      <Card className="bg-white/90 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Kaspi Pay
          </CardTitle>
          <CardDescription>
            Настройки, статистика и все транзакции Kaspi с привязкой к заказам покупателей
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="mb-6 grid w-full grid-cols-2 sm:grid-cols-4">
              <TabsTrigger value="overview" className="gap-2">
                <LayoutDashboard className="h-4 w-4" />
                Обзор
              </TabsTrigger>
              <TabsTrigger value="transactions" className="gap-2">
                <BarChart3 className="h-4 w-4" />
                Транзакции
              </TabsTrigger>
              <TabsTrigger value="logs" className="gap-2">
                <ScrollText className="h-4 w-4" />
                Логи
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <Settings className="h-4 w-4" />
                Настройки
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <KaspiOverviewTab
                token={token}
                selectedDay={selectedDay}
                onDayChange={setSelectedDay}
              />
            </TabsContent>

            <TabsContent value="transactions">
              <KaspiTransactionsTab
                token={token}
                selectedDay={selectedDay}
                onDayChange={setSelectedDay}
              />
            </TabsContent>

            <TabsContent value="logs">
              <KaspiLogsTab
                token={token}
                selectedDay={selectedDay}
                onDayChange={setSelectedDay}
              />
            </TabsContent>

            <TabsContent value="settings">
              <KaspiSettingsTab
                token={token}
                health={health}
                loading={loading}
                loadHealth={loadHealth}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
